import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { BaseTableFilters } from "@src/types";
import { clearCustomTableFilters } from "@src/features/table_filtering/utils/base_state_handler_utils";

export interface ResourcePlanOverviewFilters extends BaseTableFilters {
  project: string[];
  phase: string[];
  country: string[];
  region: string[];
  projectManager: string[];
}

export interface ResourcePlanOverviewState {
  quickFilter: string;
  customTableFilters: ResourcePlanOverviewFilters;
}

const initialState: ResourcePlanOverviewState = {
  quickFilter: "",
  customTableFilters: {
    project: [],
    phase: [],
    country: [],
    region: [],
    projectManager: [],
  },
};

const resourcePlanOverviewSlice = createSlice({
  name: "resourcePlanOverviewSlice",
  initialState,
  reducers: {
    /**
     * Sets the quick filter for the resource plan overview.
     *
     * @param state The current state of the resource plan overview.
     * @param quickSearch The quick search string to filter by.
     */
    setResourcePlanOverviewQuickFilter: (
      state: ResourcePlanOverviewState,
      quickSearch: PayloadAction<string>
    ): void => {
      state.quickFilter = quickSearch.payload;
    },

    /**
     * Sets the custom table filters for the resource plan overview.
     *
     * @param state The current state of the resource plan overview.
     * @param customTableFilters The custom filters to apply to the table (e.g., project, phase, country, region, project manager).
     */
    setResourcePlanOverviewTableFilter: (
      state: ResourcePlanOverviewState,
      customTableFilters: PayloadAction<ResourcePlanOverviewFilters>
    ): void => {
      state.customTableFilters = customTableFilters.payload;
    },

    /**
     * Clears a specific filter from the resource plan overview state.
     *
     * @param state The current state of the resource plan overview.
     * @param action The action containing the key and value to clear.
     */
    clearResourcePlanOverviewFilter: (
      state: ResourcePlanOverviewState,
      action: { payload: { key: string; value: string } }
    ) => {
      const { key, value } = action.payload;
      state.customTableFilters = clearCustomTableFilters(
        state.customTableFilters,
        key,
        value
      ) as ResourcePlanOverviewFilters;
    },

    /**
     * Clears the entire resource plan overview state, resetting it to its initial state.
     */
    clearResourcePlanOverviewState: () => initialState,
  },
});

export const {
  setResourcePlanOverviewQuickFilter,
  clearResourcePlanOverviewFilter,
  setResourcePlanOverviewTableFilter,
  clearResourcePlanOverviewState,
} = resourcePlanOverviewSlice.actions;

export default resourcePlanOverviewSlice;
